import { COLORSET_ID, COLORSET_ID_KEYS, type ColorSetId } from '../catalog/platformCatalogIds';

export type ColorWorldLocale = 'de' | 'en';

export type ColorWorldInventoryEntry = {
  /** One-line room brief for gallery chips. */
  brief: Record<ColorWorldLocale, string>;
  /**
   * Phase-0 outcome after the unforgettable-worlds pass.
   * All registry sets are `keep` once retuned / verified.
   */
  status: 'keep' | 'retune' | 'replace';
  /** Theme poster / hero chroma set (one per theme). */
  poster?: boolean;
};

/**
 * Authoritative color-world inventory (Phase 0).
 * Briefs power gallery subtitles; `poster` marks the hero set per theme.
 */
export const COLOR_WORLD_INVENTORY = {
  [COLORSET_ID.champagneNight]: {
    status: 'keep',
    poster: true,
    brief: {
      en: 'Soft anthracite salon, champagne + blush',
      de: 'Sanfter Anthrazit-Salon, Champagner + Blush',
    },
  },
  [COLORSET_ID.velvetSmoke]: {
    status: 'keep',
    brief: {
      en: 'Warm soot walls, ember jewel, brass whisper',
      de: 'Warme Rußwände, Glut-Juwel, Messinghauch',
    },
  },
  [COLORSET_ID.platinumDawn]: {
    status: 'keep',
    brief: {
      en: 'Cool blue-black anthracite, platinum ice',
      de: 'Kühles Blauanthrazit, Platin-Eis',
    },
  },
  [COLORSET_ID.ivoryChampagne]: {
    status: 'keep',
    brief: {
      en: 'Day ivory paper, champagne + platinum ink',
      de: 'Elfenbeinpapier am Tag, Champagner + Platin',
    },
  },
  [COLORSET_ID.porcelainSilver]: {
    status: 'keep',
    brief: {
      en: 'Mist porcelain, soft platinum ink, champagne spark',
      de: 'Nebeliges Porzellan, weiches Platin-Ink, Champagnerfunke',
    },
  },
  [COLORSET_ID.conservatoryChalk]: {
    status: 'keep',
    poster: true,
    brief: {
      en: 'Mist chalk glasshouse, leaf-green jewel',
      de: 'Nebel-Kreide im Wintergarten, Blattgrün',
    },
  },
  [COLORSET_ID.espalierCitrus]: {
    status: 'keep',
    brief: {
      en: 'Warm citrus espalier on glass',
      de: 'Warmes Zitrus-Spalier hinter Glas',
    },
  },
  [COLORSET_ID.duskPane]: {
    status: 'keep',
    brief: {
      en: 'Cool dusk through orangery panes',
      de: 'Kühle Dämmerung durch Orangerie-Scheiben',
    },
  },
  [COLORSET_ID.apricotTrellis]: {
    status: 'keep',
    brief: {
      en: 'Soft apricot trellis, cool glass garnish',
      de: 'Sanftes Aprikosen-Spalier, kühle Glas-Garnitur',
    },
  },
  [COLORSET_ID.frostPane]: {
    status: 'keep',
    brief: {
      en: 'Soft frost night glass, leaf spark (stance dusk)',
      de: 'Sanftes Frost-Nachtglas, Blattfunke (Dämmerung)',
    },
  },
  [COLORSET_ID.peatLinen]: {
    status: 'keep',
    poster: true,
    brief: {
      en: 'Soft peat linen, hearth rust + beeswax',
      de: 'Sanftes Torf-Leinen, Herd-Rost + Bienenwachs',
    },
  },
  [COLORSET_ID.oakSmoke]: {
    status: 'keep',
    brief: {
      en: 'Oak-smoke evening walls',
      de: 'Eichenrauch-Abendwände',
    },
  },
  [COLORSET_ID.grouseRust]: {
    status: 'keep',
    brief: {
      en: 'Copper grouse rust, cool canal garnish',
      de: 'Kupfer-Moorschneehuhn, kühler Kanal-Akzent',
    },
  },
  [COLORSET_ID.canalMist]: {
    status: 'keep',
    brief: {
      en: 'Canal mist night, cooler estate',
      de: 'Kanalnebel-Nacht, kühleres Gut',
    },
  },
  [COLORSET_ID.beeswaxGold]: {
    status: 'keep',
    brief: {
      en: 'Beeswax gold on peat linen',
      de: 'Bienenwachs-Gold auf Torf-Leinen',
    },
  },
  [COLORSET_ID.classicBrass]: {
    status: 'keep',
    poster: true,
    brief: {
      en: 'Classic brass on warm paper, gold garnish',
      de: 'Klassisches Messing auf warmem Papier, Gold',
    },
  },
  [COLORSET_ID.linenMorning]: {
    status: 'keep',
    brief: {
      en: 'Morning linen, cool sage accent',
      de: 'Morgenleinen, kühler Salbei-Akzent',
    },
  },
  [COLORSET_ID.copperGlow]: {
    status: 'keep',
    brief: {
      en: 'Copper plate glow, bottle-green garnish',
      de: 'Kupferteller-Glanz, Flaschengrün',
    },
  },
  [COLORSET_ID.zincApron]: {
    status: 'keep',
    brief: {
      en: 'Soft zinc dusk after close, brass spark',
      de: 'Sanfte Zink-Dämmerung nach Schluss, Messing',
    },
  },
  [COLORSET_ID.oliveBanquette]: {
    status: 'keep',
    brief: {
      en: 'Soft olive banquet wall, brass thread',
      de: 'Sanfte Oliven-Bankettwand, Messingfaden',
    },
  },
  [COLORSET_ID.midnightNori]: {
    status: 'keep',
    poster: true,
    brief: {
      en: 'Nori night so nigiri reads as jewels',
      de: 'Nori-Nacht, damit Nigiri wie Juwelen wirkt',
    },
  },
  [COLORSET_ID.slateCounter]: {
    status: 'keep',
    brief: {
      en: 'Cool slate counter after hours',
      de: 'Kühle Schiefer-Theke nach Feierabend',
    },
  },
  [COLORSET_ID.wasabiPaper]: {
    status: 'keep',
    brief: {
      en: 'Wasabi noon paper, ginger garnish',
      de: 'Wasabi-Mittagspapier, Ingwer-Garnitur',
    },
  },
  [COLORSET_ID.shariIvory]: {
    status: 'keep',
    brief: {
      en: 'Warm shari rice paper, soy + toasted gari',
      de: 'Warmes Shari-Reispapier, Soy + geröstetes Gari',
    },
  },
  [COLORSET_ID.cedarPlank]: {
    status: 'keep',
    brief: {
      en: 'Cedar plank night, warm wood soot',
      de: 'Zedernbrett-Nacht, warmes Holzruß',
    },
  },
  [COLORSET_ID.herbMist]: {
    status: 'keep',
    poster: true,
    brief: {
      en: 'Herb mist broth paper, chili-oil spark',
      de: 'Kräuternebel-Brühepapier, Chiliöl-Funke',
    },
  },
  [COLORSET_ID.clayBroth]: {
    status: 'keep',
    brief: {
      en: 'Clay broth bowl, warm clay + herb',
      de: 'Ton-Brüheschüssel, warmer Ton + Kraut',
    },
  },
  [COLORSET_ID.midnightLotus]: {
    status: 'keep',
    brief: {
      en: 'Midnight lotus steam, deep herb night',
      de: 'Mitternachts-Lotusdampf, tiefe Kräuternacht',
    },
  },
  [COLORSET_ID.bananaSteam]: {
    status: 'keep',
    brief: {
      en: 'Banana-leaf wrap, turmeric gold garnish',
      de: 'Bananenblatt-Wickel, Kurkuma-Gold-Garnitur',
    },
  },
  [COLORSET_ID.saffronBowl]: {
    status: 'keep',
    brief: {
      en: 'Saffron night bowl, spice steam',
      de: 'Safran-Nachtschüssel, Gewürzdampf',
    },
  },
  [COLORSET_ID.ceramicDawn]: {
    status: 'keep',
    poster: true,
    brief: {
      en: 'Ceramic shopfront dawn, cayenne spark',
      de: 'Keramik-Ladenfront im Morgengrauen, Cayenne',
    },
  },
  [COLORSET_ID.cayenneStripe]: {
    status: 'keep',
    brief: {
      en: 'Soft cayenne awning, turmeric jewel',
      de: 'Sanfter Cayenne-Markisenstreifen, Kurkuma-Juwel',
    },
  },
  [COLORSET_ID.inkShop]: {
    status: 'keep',
    brief: {
      en: 'Soft ink-shop dusk, chili jewel on tile',
      de: 'Sanfte Tintenladen-Dämmerung, Chili-Juwel',
    },
  },
  [COLORSET_ID.groutLime]: {
    status: 'keep',
    brief: {
      en: 'Grout lime day tile, chili accent',
      de: 'Fugen-Limette am Tag, Chili-Akzent',
    },
  },
  [COLORSET_ID.monsoonBlue]: {
    status: 'keep',
    brief: {
      en: 'Soft monsoon dusk, misty wet-tile blue',
      de: 'Sanfte Monsun-Dämmerung, nebliges Fliesenblau',
    },
  },
  [COLORSET_ID.emberLantern]: {
    status: 'keep',
    poster: true,
    brief: {
      en: 'Soft ember dusk wood, lantern gold',
      de: 'Sanfte Glut-Dämmerung, Laternen-Gold',
    },
  },
  [COLORSET_ID.lacquerPaper]: {
    status: 'keep',
    brief: {
      en: 'Soft lacquer cabinet, crimson + celadon stamp',
      de: 'Sanfter Lackschrank, Karmesin + Seladon-Stempel',
    },
  },
  [COLORSET_ID.inkWash]: {
    status: 'keep',
    brief: {
      en: 'Noon ink wash paper, lacquer stamp',
      de: 'Tusche-Mittagspapier, Lackstempel',
    },
  },
  [COLORSET_ID.plumSilk]: {
    status: 'keep',
    brief: {
      en: 'Soft plum dusk silk, lantern gold thread',
      de: 'Sanfte Pflaumen-Dämmerseide, Laternen-Goldfaden',
    },
  },
  [COLORSET_ID.celadonCourt]: {
    status: 'keep',
    brief: {
      en: 'Soft celadon noon court, chili spark',
      de: 'Sanfte Seladon-Mittagshof, Chili-Funke',
    },
  },
  [COLORSET_ID.espressoCore]: {
    status: 'keep',
    brief: {
      en: 'Espresso ink on bleach paper',
      de: 'Espresso-Ink auf Bleichpapier',
    },
  },
  [COLORSET_ID.paperWhite]: {
    status: 'keep',
    brief: {
      en: 'Brutal paper white, signal crumb',
      de: 'Brutales Weißpapier, Signal-Krümel',
    },
  },
  [COLORSET_ID.signalOrange]: {
    status: 'keep',
    poster: true,
    brief: {
      en: 'Construction signal orange, ink-black garnish',
      de: 'Baustellen-Signalorange, tintenschwarze Garnitur',
    },
  },
  [COLORSET_ID.concreteRose]: {
    status: 'keep',
    brief: {
      en: 'Concrete rose night soot',
      de: 'Betonrosa-Nachtruß',
    },
  },
  [COLORSET_ID.matchaGrid]: {
    status: 'keep',
    brief: {
      en: 'Bitter matcha grid, chalk line',
      de: 'Bitteres Matcha-Raster, Kreidelinie',
    },
  },
  [COLORSET_ID.terracottaDusk]: {
    status: 'keep',
    poster: true,
    brief: {
      en: 'Terracotta slip, indigo thread garnish',
      de: 'Terrakotta-Schlicker, Indigo-Faden',
    },
  },
  [COLORSET_ID.sageMist]: {
    status: 'keep',
    brief: {
      en: 'Sage mist garden paper',
      de: 'Salbeinebel-Gartenpapier',
    },
  },
  [COLORSET_ID.kilnEmber]: {
    status: 'keep',
    brief: {
      en: 'Closed kiln — terracotta fire on soot',
      de: 'Geschlossener Ofen — Terrakottafeuer auf Ruß',
    },
  },
  [COLORSET_ID.indigoLoom]: {
    status: 'keep',
    brief: {
      en: 'Indigo loom night cloth',
      de: 'Indigo-Webstuhl-Nachtstoff',
    },
  },
  [COLORSET_ID.butterSlip]: {
    status: 'keep',
    brief: {
      en: 'Butter kiln cream, cobalt oxide',
      de: 'Butter-Ofencreme, Kobalt-Oxid',
    },
  },
  [COLORSET_ID.electricViolet]: {
    status: 'keep',
    poster: true,
    brief: {
      en: 'Electric violet tube on noir soot',
      de: 'Elektrisches Violett-Rohr auf Noir-Ruß',
    },
  },
  [COLORSET_ID.cyanPulse]: {
    status: 'keep',
    brief: {
      en: 'Soft cyan day pulse, violet spark',
      de: 'Sanfter Cyan-Tagspuls, Violett-Funke',
    },
  },
  [COLORSET_ID.midnightGlass]: {
    status: 'keep',
    brief: {
      en: 'Matinee glass paper, violet ink',
      de: 'Matinee-Glaspapier, Violett-Ink',
    },
  },
  [COLORSET_ID.acidChartreuse]: {
    status: 'keep',
    brief: {
      en: 'Acid chartreuse day flyer',
      de: 'Säuregrünes Tagesflyer-Papier',
    },
  },
  [COLORSET_ID.bloodOrange]: {
    status: 'keep',
    brief: {
      en: 'Blood-orange neon, cyan pulse garnish',
      de: 'Blutorange-Neon, Cyan-Puls-Garnitur',
    },
  },
  [COLORSET_ID.burgundyLounge]: {
    status: 'keep',
    poster: true,
    brief: {
      en: 'Burgundy lounge velvet, rose garnish',
      de: 'Burgunder-Lounge-Samt, Rosen-Garnitur',
    },
  },
  [COLORSET_ID.artDecoGold]: {
    status: 'keep',
    brief: {
      en: 'Deco copper tube, rose-neon accent',
      de: 'Deco-Kupferrohr, Rosen-Neon-Akzent',
    },
  },
  [COLORSET_ID.smokeVelvet]: {
    status: 'keep',
    brief: {
      en: 'Day parlor mauve ash',
      de: 'Tagesparlour, mauvefarbene Asche',
    },
  },
  [COLORSET_ID.emeraldBooth]: {
    status: 'keep',
    brief: {
      en: 'Emerald booth, lime thread',
      de: 'Smaragd-Kabine, Limetten-Faden',
    },
  },
  [COLORSET_ID.champagneAsh]: {
    status: 'keep',
    brief: {
      en: 'Ivory ash day, burgundy jewel, sage',
      de: 'Elfenbein-Asche am Tag, Burgunderjuwel, Salbei',
    },
  },
  [COLORSET_ID.streetNeon]: {
    status: 'keep',
    poster: true,
    brief: {
      en: 'Street neon wrap on asphalt night',
      de: 'Straßen-Neonfolie auf Asphaltnacht',
    },
  },
  [COLORSET_ID.asphaltBold]: {
    status: 'keep',
    brief: {
      en: 'Cool asphalt night, ice strip + chili',
      de: 'Kühle Asphaltnacht, Eisstreifen + Chili',
    },
  },
  [COLORSET_ID.sunsetPop]: {
    status: 'keep',
    brief: {
      en: 'Magenta sunset wrap on asphalt',
      de: 'Magenta-Sonnenuntergang auf Asphalt',
    },
  },
  [COLORSET_ID.ketchupMustard]: {
    status: 'keep',
    brief: {
      en: 'Ketchup day stall, electric mint',
      de: 'Ketchup-Tagesstand, elektrisches Mint',
    },
  },
  [COLORSET_ID.pideCream]: {
    status: 'keep',
    brief: {
      en: 'Soft pide cream, sesame + sumac',
      de: 'Sanfte Pide-Creme, Sesam + Sumach',
    },
  },
  [COLORSET_ID.mustardPaper]: {
    status: 'keep',
    poster: true,
    brief: {
      en: 'Mustard rail paper, ketchup stripe',
      de: 'Senf-Schienenpapier, Ketchup-Streifen',
    },
  },
  [COLORSET_ID.tramAsphalt]: {
    status: 'keep',
    brief: {
      en: 'Tram-blue asphalt under the Hochbahn',
      de: 'Tramblaues Asphalt unter der Hochbahn',
    },
  },
  [COLORSET_ID.ketchupSignal]: {
    status: 'keep',
    brief: {
      en: 'Ketchup signal, rail-yellow garnish',
      de: 'Ketchup-Signal, Schienen-Gelb',
    },
  },
  [COLORSET_ID.pommesGold]: {
    status: 'keep',
    brief: {
      en: 'Pommes fryer gold, ketchup red',
      de: 'Pommes-Friteusen-Gold, Ketchup-Rot',
    },
  },
  [COLORSET_ID.grillChar]: {
    status: 'keep',
    brief: {
      en: 'Grill charcoal night, mustard spark',
      de: 'Grillkohle-Nacht, Senf-Funke',
    },
  },
  [COLORSET_ID.hummusPaper]: {
    status: 'keep',
    poster: true,
    brief: {
      en: 'Hummus noon cream, parsley green',
      de: 'Hummus-Mittagsceme, Petersiliengrün',
    },
  },
  [COLORSET_ID.oreganoNight]: {
    status: 'keep',
    brief: {
      en: 'Oregano soot night, chili oil',
      de: 'Oregano-Rußnacht, Chiliöl',
    },
  },
  [COLORSET_ID.lemonStall]: {
    status: 'keep',
    brief: {
      en: 'Lemon stall pith, olive leaf',
      de: 'Zitronenstand-Mark, Olivenblatt',
    },
  },
  [COLORSET_ID.sesameCream]: {
    status: 'keep',
    brief: {
      en: 'Sesame cream paper, herb garnish',
      de: 'Sesamcreme-Papier, Kräuter-Garnitur',
    },
  },
  [COLORSET_ID.zaatarDusk]: {
    status: 'keep',
    brief: {
      en: 'Zaatar dusk, pomegranate spark',
      de: 'Zaatar-Dämmerung, Granatapfel-Funke',
    },
  },
  [COLORSET_ID.coastalBlue]: {
    status: 'keep',
    poster: true,
    brief: {
      en: 'Coastal blue truck wrap, seafoam',
      de: 'Küstenblaues Truck-Wrap, Seeschaum',
    },
  },
  [COLORSET_ID.sandDune]: {
    status: 'keep',
    brief: {
      en: 'Sand dune paper, ocean accent',
      de: 'Sanddünen-Papier, Ozean-Akzent',
    },
  },
  [COLORSET_ID.forestTrail]: {
    status: 'keep',
    brief: {
      en: 'Pine canopy night, amber trail marker',
      de: 'Kiefernkronen-Nacht, Bernstein-Wegmarke',
    },
  },
  [COLORSET_ID.canyonDust]: {
    status: 'keep',
    brief: {
      en: 'Canyon dust road, sky accent',
      de: 'Canyonstaub-Straße, Himmel-Akzent',
    },
  },
  [COLORSET_ID.arcticFog]: {
    status: 'keep',
    brief: {
      en: 'Arctic fog night, cold blue',
      de: 'Arktiknebel-Nacht, kaltes Blau',
    },
  },
} as const satisfies Record<ColorSetId, ColorWorldInventoryEntry>;

export function resolveColorWorldBrief(
  colorsetId: string,
  locale: string,
): string | null {
  const entry = COLOR_WORLD_INVENTORY[colorsetId as ColorSetId];
  if (!entry) return null;
  const key: ColorWorldLocale = locale === 'en' ? 'en' : 'de';
  return entry.brief[key];
}

export function listPosterColorSetIds(): ColorSetId[] {
  return COLORSET_ID_KEYS.filter((id) => {
    const entry: ColorWorldInventoryEntry = COLOR_WORLD_INVENTORY[id];
    return entry.poster === true;
  });
}
